import { Platform } from "react-native";

import { type ThemeName, tokens } from "./tokens";

export type ThemePreference = "system" | "light" | "dark";

/**
 * Android's minimum touch target is 48 dp (Material); the web and the token file keep 44 px. Only the
 * hit area grows: controls keep their drawn size.
 */
const size =
  Platform.OS === "android"
    ? { ...tokens.size, touchTarget: 48, iconButton: 48 }
    : tokens.size;

/** Everything a component styles from: the resolved palette plus the theme-independent scales. */
function buildTheme(name: ThemeName) {
  return {
    name,
    color: tokens.color[name],
    onPrimary: tokens.color.onPrimary,
    brand: tokens.color.brand,
    shadow: tokens.shadow[name],
    font: tokens.font,
    fontSize: tokens.fontSize,
    lineHeight: tokens.lineHeight,
    letterSpacing: tokens.letterSpacing,
    space: tokens.space,
    radius: tokens.radius,
    borderWidth: tokens.borderWidth,
    size,
    breakpoint: tokens.breakpoint,
  } as const;
}

export type AppTheme = ReturnType<typeof buildTheme>;

/** One stable object per theme, so memoised styles can key on identity. */
export const themes: Record<ThemeName, AppTheme> = {
  light: buildTheme("light"),
  dark: buildTheme("dark"),
};

export function resolveTheme(
  preference: ThemePreference,
  deviceScheme: ThemeName | null,
): ThemeName {
  return preference === "system" ? (deviceScheme ?? "light") : preference;
}
